import React, { useState, useEffect } from 'react';
import { Check, Loader2, Play } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ConnectorType, ConnectorConfig } from '../types';
import { getConnectorCatalogEntry } from '../constants/connector-configs';

interface ConnectorConfigPanelProps {
  connectorType: ConnectorType;
  config: ConnectorConfig;
  onConfigChange: (config: ConnectorConfig) => void;
  onSave: () => void;
  onTestConnection?: () => Promise<{ success: boolean; missingFields?: string[] }>;
}

export const ConnectorConfigPanel: React.FC<ConnectorConfigPanelProps> = ({
  connectorType,
  config,
  onConfigChange,
  onSave,
  onTestConnection,
}) => {
  const { t } = useTranslation();
  const [localConfig, setLocalConfig] = useState<Record<string, string>>({});
  const [clearedFields, setClearedFields] = useState<Set<string>>(new Set());
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    missingFields?: string[];
    invalidFields?: { field: string; reason: string }[];
    error?: string;
  } | null>(null);

  const catalogEntry = getConnectorCatalogEntry(connectorType);

  useEffect(() => {
    const configObj = config as Record<string, any>;
    const initial: Record<string, string> = {};
    catalogEntry.configFields.forEach(field => {
      initial[field.key] = configObj[field.key] || '';
    });
    setLocalConfig(initial);
  }, [config, catalogEntry.configFields]);

  const REDACT_PREFIX = '••••••';

  const handleFieldFocus = (key: string) => {
    const val = localConfig[key] || '';
    if (val.startsWith(REDACT_PREFIX) && !clearedFields.has(key)) {
      const updated = { ...localConfig, [key]: '' };
      setLocalConfig(updated);
      setClearedFields(prev => new Set(prev).add(key));
      onConfigChange({ ...config, [key]: '' } as ConnectorConfig);
    }
  };

  const handleFieldChange = (key: string, value: string) => {
    const updated = { ...localConfig, [key]: value };
    setLocalConfig(updated);
    onConfigChange({ ...config, [key]: value } as ConnectorConfig);
  };

  return (
    <div className="flex flex-col gap-3 pt-3">
      {catalogEntry.configFields.map(field => {
        const inputId = `connector-field-${field.key}`;
        return (
          <div key={field.key}>
            <Label htmlFor={inputId} className="mb-1 block text-xs text-muted-foreground">
              {t(field.labelKey)}
            </Label>
            {field.type === 'textarea' ? (
              <Textarea
                id={inputId}
                rows={3}
                placeholder={field.placeholder}
                value={localConfig[field.key] || ''}
                onChange={(e) => handleFieldChange(field.key, e.target.value)}
              />
            ) : (
              <Input
                id={inputId}
                type={field.type === 'password' ? 'password' : 'text'}
                placeholder={field.placeholder}
                value={localConfig[field.key] || ''}
                onFocus={field.type === 'password' ? () => handleFieldFocus(field.key) : undefined}
                onChange={(e) => handleFieldChange(field.key, e.target.value)}
              />
            )}
            {field.helperTextKey && (
              <p className="mt-1 text-[11px] leading-snug text-muted-foreground">
                {t(field.helperTextKey)}
              </p>
            )}
          </div>
        );
      })}

      <div className="mt-2 flex gap-2">
        {onTestConnection && (
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            disabled={testing}
            onClick={async () => {
              setTesting(true);
              setTestResult(null);
              try {
                const result = await onTestConnection();
                setTestResult(result);
              } catch {
                setTestResult({ success: false, missingFields: [] });
              } finally {
                setTesting(false);
              }
            }}
          >
            {testing ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Play className="size-3.5" />
            )}
            {t('PAGES.API_INTEGRATION.TEST_CONNECTION')}
          </Button>
        )}
        <Button variant="outline" size="sm" className="flex-1" onClick={onSave}>
          <Check className="size-3.5" />
          {t('PAGES.API_INTEGRATION.SAVE')}
        </Button>
      </div>

      {testResult && (
        <p
          className={cn(
            'mt-0.5 text-xs font-medium',
            testResult.success ? 'text-success' : 'text-destructive',
          )}
        >
          {testResult.success
            ? t('PAGES.API_INTEGRATION.CONNECTION_SUCCESS')
            : testResult.missingFields?.length
              ? `${t('PAGES.API_INTEGRATION.MISSING_FIELDS')}: ${testResult.missingFields.join(', ')}`
              : testResult.error
                ? `${t('PAGES.API_INTEGRATION.CONNECTION_FAILED')}: ${testResult.error}`
                : t('PAGES.API_INTEGRATION.CONNECTION_FAILED')}
        </p>
      )}
    </div>
  );
};
